<template>
  <div class="tab-shell">
    <div class="tab-head">
      <h2 class="tab-title">Ordens de Compra</h2>
      <button v-if="!showForm" class="btn-primary" type="button" @click="openForm()">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Nova OC
      </button>
    </div>

    <!-- Filter tabs -->
    <div class="filter-tabs">
      <button v-for="f in statusFilters" :key="f.value" class="filter-tab" :class="{ active: statusFilter === f.value }" type="button" @click="statusFilter = f.value">{{ f.label }}</button>
    </div>

    <!-- Form -->
    <div v-if="showForm" class="form-card">
      <h3 class="form-title">{{ editId ? 'Editar Ordem de Compra' : 'Nova Ordem de Compra' }}</h3>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">Fornecedor *</span>
          <select class="field-input" v-model="form.fornecedorId">
            <option value="">Selecione...</option>
            <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.nomeFantasia || s.razaoSocial }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">Obra</span>
          <select class="field-input" v-model="form.projectId">
            <option value="">Selecione...</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">Prazo de Entrega</span>
          <input class="field-input" type="date" v-model="form.prazoEntrega" />
        </label>
        <label class="field">
          <span class="field-label">Condição de Pagamento</span>
          <input class="field-input" v-model="form.condicaoPagamento" placeholder="Ex: 30 dias, boleto" />
        </label>
        <label class="field field--full">
          <span class="field-label">Observações</span>
          <textarea class="field-input field-textarea" v-model="form.observacoes" rows="2" placeholder="Notas adicionais..."></textarea>
        </label>
      </div>

      <!-- Items -->
      <div class="items-section">
        <div class="items-header">
          <span class="items-title">Itens</span>
          <button class="btn-add-item" type="button" @click="addItem">+ Adicionar item</button>
        </div>
        <div v-for="(item, idx) in form.items" :key="idx" class="item-row">
          <input class="field-input item-desc" v-model="item.descricao" placeholder="Descrição do item" />
          <input class="field-input item-qty" type="number" v-model.number="item.quantidade" min="1" placeholder="Qtd" />
          <input class="field-input item-unit" v-model="item.unidade" placeholder="Un." />
          <input class="field-input item-value" type="number" v-model.number="item.valorUnitario" min="0" step="0.01" placeholder="Vlr unit." />
          <button class="btn-remove-item" type="button" @click="removeItem(idx)">✕</button>
        </div>
        <div v-if="form.items.length === 0" class="items-empty-hint">Nenhum item adicionado.</div>
      </div>

      <p v-if="formError" class="form-error">{{ formError }}</p>
      <div class="form-actions">
        <button class="btn-save" type="button" :disabled="saving" @click="save">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
        <button class="btn-cancel" type="button" @click="closeForm">Cancelar</button>
      </div>
    </div>

    <!-- List -->
    <div v-if="loading" class="empty-hint">Carregando...</div>
    <div v-else-if="filteredOcs.length === 0 && !showForm" class="empty-hint">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
      Nenhuma ordem de compra.
    </div>
    <div v-else class="cards-grid">
      <article v-for="oc in filteredOcs" :key="oc.id" class="oc-card">
        <div class="card-body">
          <div class="card-header-row">
            <span class="card-title">OC #{{ oc.numero || oc.id.slice(-6).toUpperCase() }}</span>
            <span class="status-chip" :class="ocStatusClass(oc.status)">{{ ocStatusLabel(oc.status) }}</span>
          </div>
          <div class="card-meta" v-if="oc.fornecedorNome">{{ oc.fornecedorNome }}</div>
          <div class="card-meta" v-if="oc.prazoEntrega">Entrega: {{ formatDate(oc.prazoEntrega) }}</div>
          <div class="card-meta" v-if="oc.condicaoPagamento">Pagto: {{ oc.condicaoPagamento }}</div>
          <div class="card-value-row" v-if="oc.valorTotal">{{ formatCurrency(oc.valorTotal) }}</div>
        </div>
        <div class="card-footer">
          <button class="card-btn" type="button" @click="openForm(oc)">Editar</button>
          <button v-if="oc.status === 'emitida'" class="card-btn card-btn--ok" type="button" @click="updateStatus(oc.id, 'confirmada')">Confirmar</button>
          <button v-if="oc.status === 'confirmada'" class="card-btn card-btn--ok" type="button" @click="updateStatus(oc.id, 'entregue')">Entregue</button>
          <button v-if="oc.status !== 'cancelada' && oc.status !== 'entregue'" class="card-btn card-btn--warn" type="button" @click="updateStatus(oc.id, 'cancelada')">Cancelar</button>
        </div>
      </article>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { projectsStore } from '@/store/projects'

interface Supplier { id: string; razaoSocial: string; nomeFantasia: string }
interface OcItem { descricao: string; quantidade: number; unidade: string; valorUnitario: number }
interface Oc {
  id: string; numero: string; fornecedorId: string; fornecedorNome: string
  projectId: string; prazoEntrega: string; condicaoPagamento: string
  observacoes: string; status: string; valorTotal: number
}

const emptyForm = () => ({
  fornecedorId: '', projectId: '', prazoEntrega: '', condicaoPagamento: '',
  observacoes: '', items: [] as OcItem[]
})

const OC_STATUS_LABELS: Record<string, string> = {
  emitida: 'Emitida', confirmada: 'Confirmada', entregue: 'Entregue', cancelada: 'Cancelada'
}
const OC_STATUS_CLASS: Record<string, string> = {
  emitida: 'chip--blue', confirmada: 'chip--yellow', entregue: 'chip--green', cancelada: 'chip--red'
}

export default defineComponent({
  name: 'OfficeOrdemCompraTab',
  setup() {
    const ocs = ref<Oc[]>([])
    const suppliers = ref<Supplier[]>([])
    const loading = ref(false)
    const showForm = ref(false)
    const editId = ref('')
    const form = ref(emptyForm())
    const formError = ref('')
    const saving = ref(false)
    const statusFilter = ref('')

    const projects = computed(() => projectsStore.all())

    const statusFilters = [
      { value: '', label: 'Todas' },
      { value: 'emitida', label: 'Emitida' },
      { value: 'confirmada', label: 'Confirmada' },
      { value: 'entregue', label: 'Entregue' },
      { value: 'cancelada', label: 'Cancelada' }
    ]

    const filteredOcs = computed(() =>
      statusFilter.value ? ocs.value.filter(o => o.status === statusFilter.value) : ocs.value
    )

    const load = async () => {
      loading.value = true
      try {
        const [ocData, supData] = await Promise.allSettled([
          apiFetch<Oc[]>('/office/purchase-orders'),
          apiFetch<Supplier[]>('/office/suppliers')
        ])
        if (ocData.status === 'fulfilled') ocs.value = ocData.value
        if (supData.status === 'fulfilled') suppliers.value = supData.value
      } finally { loading.value = false }
    }

    const openForm = (oc?: Oc) => {
      if (oc) {
        editId.value = oc.id
        form.value = { fornecedorId: oc.fornecedorId, projectId: oc.projectId, prazoEntrega: oc.prazoEntrega, condicaoPagamento: oc.condicaoPagamento, observacoes: oc.observacoes, items: [] }
      } else {
        editId.value = ''; form.value = emptyForm()
      }
      formError.value = ''; showForm.value = true
    }
    const closeForm = () => { showForm.value = false; editId.value = ''; formError.value = '' }

    const addItem = () => form.value.items.push({ descricao: '', quantidade: 1, unidade: 'un', valorUnitario: 0 })
    const removeItem = (idx: number) => form.value.items.splice(idx, 1)

    const save = async () => {
      if (!form.value.fornecedorId) { formError.value = 'Selecione um fornecedor.'; return }
      saving.value = true; formError.value = ''
      try {
        if (editId.value) await apiFetch(`/office/purchase-orders/${editId.value}`, { method: 'PUT', body: JSON.stringify(form.value) })
        else await apiFetch('/office/purchase-orders', { method: 'POST', body: JSON.stringify(form.value) })
        uiStore.toast(editId.value ? 'OC atualizada.' : 'OC criada.', 'success')
        closeForm(); await load()
      } catch (e: unknown) { formError.value = e instanceof Error ? e.message : 'Erro ao salvar.' }
      finally { saving.value = false }
    }

    const updateStatus = async (id: string, status: string) => {
      try {
        await apiFetch(`/office/purchase-orders/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) })
        uiStore.toast(`Status atualizado.`, 'success'); await load()
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro.', 'error') }
    }

    const ocStatusLabel = (s: string) => OC_STATUS_LABELS[s] || s
    const ocStatusClass = (s: string) => OC_STATUS_CLASS[s] || ''
    const formatDate = (d: string) => {
      const m = `${d || ''}`.match(/(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[3]}/${m[2]}/${m[1]}` : d
    }
    const formatCurrency = (v: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

    onMounted(() => {
      load()
      if (!projectsStore.all().length) projectsStore.refresh().catch(() => { /* ignore */ })
    })

    return {
      ocs, suppliers, projects, loading, showForm, editId, form, formError, saving,
      statusFilter, statusFilters, filteredOcs, openForm, closeForm, addItem, removeItem,
      save, updateStatus, ocStatusLabel, ocStatusClass, formatDate, formatCurrency
    }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 13px; font-weight: 600; &:hover { opacity: .88; } }
.filter-tabs { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 20px; }
.filter-tab { padding: 6px 13px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.12); background: #fff; cursor: pointer; font-size: 12px; font-weight: 500; color: var(--ink-muted, #666); transition: all .12s; &:hover { background: rgba(31,30,26,.04); } &.active { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); border-color: transparent; } }
.form-card { background: var(--card, #fff); border: 1px solid rgba(31,30,26,.1); border-radius: 14px; padding: 20px; margin-bottom: 24px; }
.form-title { margin: 0 0 16px; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field--full { grid-column: 1 / -1; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input { padding: 8px 11px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18); font-size: 13px; outline: none; background: #fff; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.field-textarea { resize: vertical; }
.items-section { border: 1px solid rgba(31,30,26,.1); border-radius: 10px; padding: 12px; margin-bottom: 16px; }
.items-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.items-title { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); }
.btn-add-item { padding: 5px 12px; border-radius: 6px; border: 1.5px solid rgba(31,30,26,.15); background: #fff; cursor: pointer; font-size: 12px; font-weight: 600; &:hover { background: rgba(31,30,26,.04); } }
.item-row { display: grid; grid-template-columns: 2fr 70px 60px 100px 28px; gap: 6px; margin-bottom: 8px; align-items: center; }
.item-desc, .item-qty, .item-unit, .item-value { padding: 6px 8px; border-radius: 6px; border: 1.5px solid rgba(31,30,26,.15); font-size: 12px; outline: none; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.btn-remove-item { background: none; border: none; cursor: pointer; color: var(--ink-muted, #aaa); font-size: 14px; padding: 0; &:hover { color: #dc2626; } }
.items-empty-hint { font-size: 12px; color: var(--ink-muted, #aaa); text-align: center; padding: 8px 0; }
.form-error { font-size: 12px; color: #dc2626; margin: 8px 0 0; }
.form-actions { display: flex; gap: 8px; }
.btn-save { padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer; background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600; &:disabled { opacity: .6; cursor: not-allowed; } &:hover:not(:disabled) { opacity: .88; } }
.btn-cancel { padding: 8px 14px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18); background: #fff; cursor: pointer; font-size: 13px; font-weight: 500; &:hover { background: rgba(31,30,26,.04); } }
.empty-hint { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px; svg { opacity: .35; } }
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.oc-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card, #fff); display: flex; flex-direction: column; }
.card-body { padding: 14px 16px 10px; flex: 1; display: flex; flex-direction: column; gap: 4px; }
.card-header-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.card-title { font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); }
.card-meta { font-size: 12px; color: var(--ink-muted, #888); }
.card-value-row { font-size: 16px; font-weight: 800; color: var(--ink, #2c2b27); margin-top: 4px; }
.status-chip { display: inline-block; font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 20px; &.chip--blue { background: #dbeafe; color: #1d4ed8; } &.chip--yellow { background: #fef9c3; color: #854d0e; } &.chip--green { background: #dcfce7; color: #15803d; } &.chip--red { background: #fef2f2; color: #dc2626; } }
.card-footer { display: flex; gap: 4px; padding: 8px 10px; border-top: 1px solid rgba(31,30,26,.07); flex-wrap: wrap; }
.card-btn { display: inline-flex; align-items: center; padding: 5px 9px; border-radius: 6px; border: none; cursor: pointer; background: transparent; color: var(--ink-muted, #666); font-size: 11px; font-weight: 500; transition: background .12s; &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); } &--ok { color: #15803d; &:hover { background: #dcfce7; } } &--warn { color: #c2410c; &:hover { background: #fff7ed; } } }
@media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } .item-row { grid-template-columns: 1fr 1fr; } }
</style>
